import { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import ButtonSaveForm from '../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../components/buttons/ButtonCancelForm';
import InputField from '../../components/InputField';
import TextAreaField from '../../components/TextAreaField';
import LazyLoading from '../../components/LazyLoading';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useChecklistService from '../../app/services/hooks/useChecklistService';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import { AppContext } from '../../contexts/AppContext';
import '../../styles/page-components.css';

interface Question {
    id?: number;
    question_text: string;
    help_text: string;
    order: number;
    is_required: boolean;
    is_active: boolean;
    allow_multiple: boolean;
    options: Option[];
}

interface Option {
    id?: number;
    option_text: string;
    score: number;
    order: number;
}

interface ScoringRange {
    min: number;
    max: number;
    label: string;
    color: string;
}

const defaultScoringRanges: Record<string, ScoringRange> = {
    excellent: { min: 80, max: 100, label: 'Excelente', color: '#28a745' },
    good: { min: 60, max: 79, label: 'Bueno', color: '#17a2b8' },
    regular: { min: 40, max: 59, label: 'Regular', color: '#ffc107' },
    bad: { min: 0, max: 39, label: 'Necesita mejorar', color: '#dc3545' }
};

const ChecklistForm = () => {
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();
    const isEdit = Boolean(id);
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { 
        showChecklist, 
        storeChecklist, 
        updateChecklist,
        fetchingShowChecklist,
        fetchingStoreChecklist,
        fetchingUpdateChecklist
    } = useChecklistService();

    const [form, setForm] = useState({
        name: '',
        description: '',
        instructions: '',
        ai_report_prompt: '',
        scoring_ranges: defaultScoringRanges,
        related_services: [] as string[]
    });

    const [questions, setQuestions] = useState<Question[]>([
        {
            question_text: '',
            help_text: '',
            order: 0,
            is_required: true,
            is_active: true,
            allow_multiple: true,
            options: [
                { option_text: '', score: 0, order: 0 },
                { option_text: '', score: 0, order: 1 }
            ]
        }
    ]);

    const breadcrumbs = [
        { label: 'Inicio', path: '/intranet' },
        { label: 'Gestión de Checklists', path: '/intranet/checklists-admin' },
        { label: isEdit ? 'Editar Checklist' : 'Nuevo Checklist', isCurrent: true }
    ];

    useEffect(() => {
        if (isEdit && id) {
            loadChecklist(parseInt(id));
        }
    }, [id]);

    const loadChecklist = (checklistId: number) => {
        showChecklist(checklistId, {
            onSuccess: (response: ServiceResponse) => {
                const checklist = response.data.checklist;
                setForm({
                    name: checklist.name || '',
                    description: checklist.description || '',
                    instructions: checklist.instructions || '',
                    ai_report_prompt: checklist.ai_report_prompt || '',
                    scoring_ranges: checklist.scoring_ranges || defaultScoringRanges,
                    related_services: checklist.related_services || []
                });
                if (checklist.questions && checklist.questions.length > 0) {
                    setQuestions(checklist.questions.map((q: any, idx: number) => ({
                        id: q.id,
                        question_text: q.question_text,
                        help_text: q.help_text || '',
                        order: q.order || idx,
                        is_required: q.is_required,
                        is_active: q.is_active,
                        allow_multiple: q.allow_multiple || false,
                        options: q.options.map((o: any, oIdx: number) => ({
                            id: o.id,
                            option_text: o.option_text,
                            score: o.score,
                            order: o.order || oIdx
                        }))
                    })));
                }
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                history.push('/intranet/checklists-admin');
            }
        });
    };

    const handleInputChange = (field: string) => (value: any) => {
        setForm({ ...form, [field]: value });
    };

    const handleQuestionChange = (qIndex: number, field: string, value: any) => {
        const updated = [...questions];
        (updated[qIndex] as any)[field] = value;
        setQuestions(updated);
    };

    const handleOptionChange = (qIndex: number, oIndex: number, field: string, value: any) => {
        const updated = [...questions];
        (updated[qIndex].options[oIndex] as any)[field] = value;
        setQuestions(updated);
    };

    const addQuestion = () => {
        setQuestions([
            ...questions,
            {
                question_text: '',
                help_text: '',
                order: questions.length,
                is_required: true,
                is_active: true,
                allow_multiple: true,
                options: [
                    { option_text: '', score: 0, order: 0 },
                    { option_text: '', score: 0, order: 1 }
                ]
            }
        ]);
    };

    const removeQuestion = (qIndex: number) => {
        if (questions.length <= 1) {
            toast.warning('El checklist debe tener al menos una pregunta.');
            return;
        }
        setQuestions(questions.filter((_, i) => i !== qIndex));
    };

    const addOption = (qIndex: number) => {
        const updated = [...questions];
        updated[qIndex].options.push({
            option_text: '',
            score: 0,
            order: updated[qIndex].options.length
        });
        setQuestions(updated);
    };

    const removeOption = (qIndex: number, oIndex: number) => {
        if (questions[qIndex].options.length <= 2) {
            toast.warning('Cada pregunta debe tener al menos 2 opciones.');
            return;
        }
        const updated = [...questions];
        updated[qIndex].options = updated[qIndex].options.filter((_, i) => i !== oIndex);
        setQuestions(updated);
    };

    const handleSubmit = () => {
        // Validaciones básicas
        if (!form.name.trim()) {
            toast.error('El nombre del checklist es obligatorio.');
            return;
        }
        if (!form.description.trim()) {
            toast.error('La descripción del checklist es obligatoria.');
            return;
        }
        if (questions.some(q => !q.question_text.trim())) {
            toast.error('Todas las preguntas deben tener texto.');
            return;
        }
        if (questions.some(q => q.options.some(o => !o.option_text.trim()))) {
            toast.error('Todas las opciones deben tener texto.');
            return;
        }

        const data = {
            ...form,
            questions: questions.map((q, qIdx) => ({
                ...q,
                order: qIdx,
                options: q.options.map((o, oIdx) => ({
                    ...o,
                    order: oIdx
                }))
            }))
        };

        if (showLoading) showLoading('loading', isEdit ? 'Actualizando...' : 'Creando...');

        const action = isEdit 
            ? updateChecklist(parseInt(id!), data, {
                onSuccess: handleSuccess,
                onError: handleError,
                onFieldError: handleFieldError
            })
            : storeChecklist(data, {
                onSuccess: handleSuccess,
                onError: handleError,
                onFieldError: handleFieldError
            });
    };

    const handleSuccess = (response: ServiceResponse) => {
        if (hideLoading) hideLoading();
        toast.success(response.message);
        setErrors({});
        history.push('/intranet/checklists-admin');
    };

    const handleError = (response: ServiceResponse) => {
        if (hideLoading) hideLoading();
        toast.error(response.message);
    };

    const handleFieldError = (response: ServiceResponse) => {
        if (hideLoading) hideLoading();
        setErrors(response.data);
        toast.error('Por favor corrige los errores en el formulario.');
    };

    if (fetchingShowChecklist) {
        return (
            <div className="container-fluid">
                <div className="page-container">
                    <LazyLoading height={400} />
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="page-container">
                <PageHeader title="" breadcrumbs={breadcrumbs} />

                <DefaultCard>
                    <h1 className="page-title mb-4">
                        {isEdit ? 'Editar Checklist' : 'Nuevo Checklist'}
                    </h1>

                    {/* Información básica */}
                    <div className="mb-4">
                        <h5 className="fw-bold mb-3">
                            <i className="fas fa-info-circle me-2"></i>
                            Información General
                        </h5>
                        <div className="row">
                            <div className="col-12 mb-3">
                                <InputField
                                    label="Nombre del Checklist"
                                    value={form.name}
                                    onChange={handleInputChange('name')}
                                    onFocus={() => onFocusRemove('name')}
                                    error={fieldErrorMessage('name')}
                                    placeholder="Ej: Evaluación de Protección de Marca"
                                    required
                                />
                            </div>
                            <div className="col-12 mb-3">
                                <TextAreaField
                                    label="Descripción"
                                    value={form.description}
                                    onChange={handleInputChange('description')}
                                    onFocus={() => onFocusRemove('description')}
                                    error={fieldErrorMessage('description')}
                                    placeholder="Describe para qué es este checklist y qué evalúa..."
                                    rows={3}
                                    required
                                />
                            </div>
                            <div className="col-12 mb-3">
                                <TextAreaField
                                    label="Instrucciones para el usuario (opcional)"
                                    value={form.instructions}
                                    onChange={handleInputChange('instructions')}
                                    onFocus={() => onFocusRemove('instructions')}
                                    error={fieldErrorMessage('instructions')}
                                    placeholder="Instrucciones que verá el usuario antes de comenzar..."
                                    rows={2}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Preguntas */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0">
                                <i className="fas fa-question-circle me-2 text-primary"></i>
                                Preguntas ({questions.length})
                            </h5>
                            <ButtonGeneral
                                onClick={addQuestion}
                                title="Agregar Pregunta"
                                variant="outline"
                                size="small"
                                icon="fa-plus"
                            />
                        </div>

                        {questions.map((question, qIndex) => (
                            <div 
                                key={qIndex} 
                                className="border rounded mb-4"
                                style={{ 
                                    backgroundColor: '#fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                            >
                                {/* Encabezado de la pregunta */}
                                <div 
                                    className="d-flex justify-content-between align-items-center px-3 py-2"
                                    style={{ 
                                        backgroundColor: '#f8f9fa',
                                        borderBottom: '1px solid #e9ecef',
                                        borderRadius: '0.375rem 0.375rem 0 0'
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <span 
                                            className="badge me-2"
                                            style={{ 
                                                backgroundColor: '#3b82f6',
                                                fontSize: '0.85rem',
                                                padding: '0.4em 0.8em'
                                            }}
                                        >
                                            {qIndex + 1}
                                        </span>
                                        <span className="fw-semibold text-secondary">Pregunta</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm text-danger"
                                        onClick={() => removeQuestion(qIndex)}
                                        title="Eliminar pregunta"
                                        style={{ 
                                            border: 'none',
                                            backgroundColor: 'transparent'
                                        }}
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </div>

                                {/* Contenido de la pregunta */}
                                <div className="p-3">
                                    <div className="mb-3">
                                        <TextAreaField
                                            label="Texto de la pregunta"
                                            value={question.question_text}
                                            onChange={(value) => handleQuestionChange(qIndex, 'question_text', value)}
                                            onFocus={() => onFocusRemove(`questions.${qIndex}.question_text`, true)}
                                            error={fieldErrorMessage(`questions.${qIndex}.question_text`, true)}
                                            placeholder="Ej: ¿Tu empresa tiene una marca registrada actualmente?"
                                            rows={2}
                                            required
                                        />
                                    </div>

                                    {/* Toggle selección múltiple */}
                                    <div className="mb-3 d-flex align-items-center gap-2">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`allow_multiple_${qIndex}`}
                                                checked={question.allow_multiple}
                                                onChange={(e) => handleQuestionChange(qIndex, 'allow_multiple', e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor={`allow_multiple_${qIndex}`}>
                                                <i className={`fas ${question.allow_multiple ? 'fa-check-square' : 'fa-dot-circle'} me-1`}></i>
                                                {question.allow_multiple ? 'Selección múltiple (checkboxes)' : 'Selección única (radio)'}
                                            </label>
                                        </div>
                                    </div>

                                    {/* Opciones de respuesta */}
                                    <div 
                                        className="p-3 rounded"
                                        style={{ backgroundColor: '#f8fafc' }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <span className="fw-semibold text-secondary" style={{ fontSize: '0.9rem' }}>
                                                <i className="fas fa-list-ul me-2"></i>
                                                Opciones de respuesta
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={() => addOption(qIndex)}
                                                style={{
                                                    backgroundColor: '#e2e8f0',
                                                    border: 'none',
                                                    color: '#475569',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                <i className="fas fa-plus me-1"></i> Agregar opción
                                            </button>
                                        </div>

                                        {/* Header de opciones */}
                                        <div className="row mb-2 px-2">
                                            <div className="col-7">
                                                <small className="text-muted fw-semibold">Texto de la opción</small>
                                            </div>
                                            <div className="col-3">
                                                <small className="text-muted fw-semibold">Puntaje</small>
                                            </div>
                                            <div className="col-2"></div>
                                        </div>

                                        {question.options.map((option, oIndex) => (
                                            <div 
                                                key={oIndex} 
                                                className="row mb-2 align-items-center p-2 rounded"
                                                style={{ backgroundColor: '#fff' }}
                                            >
                                                <div className="col-7">
                                                    <InputField
                                                        label=""
                                                        value={option.option_text}
                                                        onChange={(value) => handleOptionChange(qIndex, oIndex, 'option_text', value)}
                                                        onFocus={() => onFocusRemove(`questions.${qIndex}.options.${oIndex}.option_text`, true)}
                                                        error={fieldErrorMessage(`questions.${qIndex}.options.${oIndex}.option_text`, true)}
                                                        placeholder={`Ej: Sí, tengo marca registrada`}
                                                    />
                                                </div>
                                                <div className="col-3">
                                                    <InputField
                                                        label=""
                                                        type="number"
                                                        value={option.score.toString()}
                                                        onChange={(value) => handleOptionChange(qIndex, oIndex, 'score', parseInt(value) || 0)}
                                                        onFocus={() => onFocusRemove(`questions.${qIndex}.options.${oIndex}.score`, true)}
                                                        error={fieldErrorMessage(`questions.${qIndex}.options.${oIndex}.score`, true)}
                                                        placeholder="0"
                                                    />
                                                </div>
                                                <div className="col-2 d-flex justify-content-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm"
                                                        onClick={() => removeOption(qIndex, oIndex)}
                                                        title="Eliminar opción"
                                                        style={{
                                                            backgroundColor: '#fee2e2',
                                                            border: 'none',
                                                            color: '#dc2626',
                                                            width: '32px',
                                                            height: '32px',
                                                            padding: '0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: '6px'
                                                        }}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Botones */}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <ButtonCancelForm callbackCancel={() => history.push('/intranet/checklists-admin')} />
                        <ButtonSaveForm 
                            callbackSave={handleSubmit} 
                            loading={fetchingStoreChecklist || fetchingUpdateChecklist}
                            title={isEdit ? 'Actualizar' : 'Crear Checklist'}
                        />
                    </div>
                </DefaultCard>
            </div>
        </div>
    );
};

export default ChecklistForm;

